/**
 * The account-security client: two-factor setup, password change, device sessions.
 *
 * Auth answers on the hub's `/api/auth/*`, which the dev server proxies to its own
 * origin — the same door the shared sign-in card uses, so no absolute origin is needed.
 * A session-token registry sits behind `/sessions`, so devices can be listed and
 * revoked, and `/2fa/*` rides the ticket the gate's password step now hands over.
 */

async function authFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(
      (data.error as string) || (data.message as string) || `Request failed with ${res.status}`,
    );
  }
  return data as T;
}

/* ---------------------------------------------------------------- two factor */

export async function setupTwoFactor(): Promise<{ secret: string; otpauth: string }> {
  return authFetch('/api/auth/2fa/setup', { method: 'POST' });
}

export async function enableTwoFactor(code: string): Promise<{ enabled: boolean; recoveryCodes: string[] }> {
  return authFetch('/api/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
}

export async function disableTwoFactor(code: string): Promise<{ enabled: boolean }> {
  return authFetch('/api/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
}

/* ------------------------------------------------------------------ password */

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return authFetch('/api/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });
}

/* ------------------------------------------------------------------ sessions */

export interface SessionRow {
  id: string;
  userAgent: string;
  ip: string;
  createdAt: string;
  lastSeenAt: string;
  revoked: boolean;
  current: boolean;
}

export async function listSessions(): Promise<SessionRow[]> {
  const data = await authFetch<{ sessions: SessionRow[] }>('/api/auth/sessions');
  return data.sessions ?? [];
}

export async function revokeSession(sessionId: string): Promise<void> {
  await authFetch(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
}

export async function revokeOtherSessions(): Promise<{ ok: boolean; revoked: number }> {
  return authFetch('/api/auth/sessions/revoke-others', { method: 'POST' });
}

/* --------------------------------------------------------- persistent alerts */

/**
 * The suite's shared alert pool is the server-side home of notifications: durable,
 * per-user, and pushed live as `alert:new` on the socket. Only drive events arrive
 * here (their `externalId` is prefixed `sak:`), so the bell stays about the drive.
 */

export interface DriveAlert {
  id: string;
  kind: string;
  title: string;
  body?: string;
  externalId: string;
  createdAt: string;
  readAt?: string | null;
}

export async function listAlerts(limit = 50): Promise<DriveAlert[]> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`/api/alerts?limit=${limit}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Alerts failed with ${res.status}`);
  const data = (await res.json().catch(() => ({}))) as { items?: DriveAlert[] };
  return (data.items ?? []).filter((alert) => typeof alert.externalId === 'string' && alert.externalId.startsWith('sak:'));
}

export async function markAlertRead(alertId: string): Promise<void> {
  await authFetch(`/api/alerts/${alertId}/read`, { method: 'POST' });
}

export async function markAllAlertsRead(): Promise<void> {
  await authFetch('/api/alerts/read', { method: 'POST' });
}
